/**
 * CR202: Header Preview Indicator
 * 
 * Shows a compact preview of what the header will display.
 */

import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Image, Type, AlignLeft, AlertTriangle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import type { HeaderValidationResult } from "@/lib/layout-studio/headerValidation";
import type { AppAsset } from "@/lib/asset-studio/types";
import { sanitizeSvg } from "@/lib/asset-studio/types";
import { getAssetImageSrc } from "@/lib/assets/getAppAssetPublicUrl";

interface HeaderPreviewIndicatorProps {
  enabled: boolean;
  hasLogo: boolean;
  hasTitle: boolean;
  hasSubtitle: boolean;
  effectiveTitle: string;
  effectiveSubtitle: string;
  logoAsset: AppAsset | null | undefined;
  validation: HeaderValidationResult;
}

export function HeaderPreviewIndicator({
  enabled,
  hasLogo,
  hasTitle,
  hasSubtitle,
  effectiveTitle,
  effectiveSubtitle,
  logoAsset,
  validation,
}: HeaderPreviewIndicatorProps) {
  if (!enabled) {
    return (
      <div className="rounded-md border border-dashed p-2 bg-muted/30">
        <p className="text-[10px] text-muted-foreground text-center">
          Header block is disabled
        </p>
      </div>
    );
  }
  
  // Show warnings
  if (validation.warnings.length > 0) {
    return (
      <div className="space-y-2">
        {validation.warnings.map((warning, i) => (
          <Alert 
            key={i} 
            variant={warning.severity === "error" ? "destructive" : "default"}
            className="py-2 px-3"
          >
            <AlertTriangle className="h-3 w-3" />
            <AlertDescription className="text-[10px] flex items-center justify-between gap-2">
              <span>{warning.message}</span>
              {warning.type === "missing_logo_asset" && (
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className="h-5 px-2 text-[10px]"
                >
                  <Link to="/admin/asset-studio">
                    Asset Studio
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Link>
                </Button>
              )}
            </AlertDescription>
          </Alert>
        ))}
        
        {/* Still show preview state */}
        <HeaderPreviewContent
          hasLogo={hasLogo}
          hasTitle={hasTitle}
          hasSubtitle={hasSubtitle}
          effectiveTitle={effectiveTitle}
          effectiveSubtitle={effectiveSubtitle}
          logoAsset={logoAsset}
        />
      </div>
    );
  }
  
  return (
    <HeaderPreviewContent
      hasLogo={hasLogo}
      hasTitle={hasTitle}
      hasSubtitle={hasSubtitle}
      effectiveTitle={effectiveTitle}
      effectiveSubtitle={effectiveSubtitle}
      logoAsset={logoAsset}
    />
  );
}

function HeaderPreviewContent({
  hasLogo,
  hasTitle,
  hasSubtitle,
  effectiveTitle,
  effectiveSubtitle,
  logoAsset,
}: {
  hasLogo: boolean;
  hasTitle: boolean;
  hasSubtitle: boolean;
  effectiveTitle: string;
  effectiveSubtitle: string;
  logoAsset: AppAsset | null | undefined;
}) {
  const hasAnyContent = hasLogo || hasTitle || hasSubtitle;
  
  if (!hasAnyContent) {
    return null; // Warning is shown separately
  }
  
  return (
    <div className="rounded-md border bg-muted/20 p-2 space-y-1.5">
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-medium">
        <AlignLeft className="h-3 w-3" />
        Header Preview
      </div>
      
      <div className="flex items-center gap-2 flex-wrap">
        {hasLogo && (
          <div className="flex items-center gap-1">
            {/* CR201/CR282: Use img with resolved URL */}
            {logoAsset ? (
              <img 
                src={getAssetImageSrc(logoAsset) || ""}
                alt={logoAsset.name}
                className="w-5 h-5 flex-shrink-0 object-contain"
              />
            ) : (
              <div className="w-5 h-5 bg-muted rounded flex items-center justify-center">
                <Image className="h-3 w-3 text-muted-foreground" />
              </div>
            )}
            <Badge variant="outline" className="text-[8px] px-1 py-0">
              Logo
            </Badge>
          </div>
        )}
        
        {hasTitle && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 max-w-[120px] truncate">
            <Type className="h-2.5 w-2.5 mr-1" />
            {effectiveTitle || "Title"}
          </Badge>
        )}
        
        {hasSubtitle && (
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 max-w-[150px] truncate">
            {effectiveSubtitle || "Subtitle"}
          </Badge>
        )}
      </div>
    </div>
  );
}
